import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Browsers } from "@phosphor-icons/react";
import { Page } from "@/ui/Layout/Page";
import Button from "@/ui/Button";
import ListDropdown from "@/ui/ListDropdown";
import { DownCarrotIcon } from "@/ui/Icons";
import { ButtonTypes, Variants } from "@/ui/types";
import { StatusPill, statusOf } from "./StatusPill";
import { CodeView } from "./CodeView";
import { BrowserWrapper, BrowserSize } from "@/Apps/Pages/Editor/BrowserWrapper";
import MobileResponsiveToggle from "@/Apps/Pages/MobileResponsiveToggle";
import { MobileState } from "@/Apps/Pages/MobileResponsiveToggle/types";
import { getCampaign } from "@/api/campaigns";
import { getPage, updatePage } from "@/api/pages";
import { getSessionToken } from "@/api/auth";
import { DeleteHtmlPageModal } from "./DeleteHtmlPageModal";
import { PublishHtmlModal } from "./PublishHtmlModal";
import { RenameHtmlPageModal } from "./RenameHtmlPageModal";
import { PageTrackingCodesModal } from "@/Apps/Pages/Popups/PageTrackingCodesModal";

// AMB-697 — an uploaded page, shown with the same chrome as the Launch Pages editor (app
// sidebar, breadcrumb top bar, device switcher, Actions menu, Publish) and the same
// browser-frame canvas. Preview shows the uploaded page as-is (tracking is only added at
// publish time, so previews never count as visits). Route: /sites/:campaignId/html/:pageId

export const HtmlPageView = () => {
  const { campaignId = "", pageId = "" } = useParams();
  const navigate = useNavigate();
  const token = getSessionToken() || "";
  const [site, setSite] = useState<any>(null);
  const [page, setPage] = useState<any>(null);
  const [showCode, setShowCode] = useState(false);
  const [mobileState, setMobileState] = useState<{ type: MobileState }>({ type: MobileState.DESKTOP });
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const siteHref = `/sites/${campaignId}`;
  const siteAddress = site?.domain?.name;

  const load = async () => {
    const response = await getPage(pageId, { token });
    if (response?.data) setPage(response.data);
    else setMessage(response?.error || "This page couldn't be loaded.");
  };

  useEffect(() => {
    load();
    (async () => setSite(await getCampaign({ token, id: campaignId })))();
  }, [pageId, campaignId]);

  const live = Boolean(page?.pageSettings?.html?.publishedSlug);

  const replaceWith = async (file?: File | null) => {
    if (!file) return;
    if (!/\.html?$/i.test(file.name)) return setMessage("Choose a page file (.html).");
    setBusy("Replacing…");
    const html = await file.text();
    const response = await updatePage(pageId, { token, settings: { htmlContent: html, fileName: file.name } });
    setBusy("");
    if (!response || response.error) return setMessage(response?.error || "The file couldn't be saved. Try again.");
    setPage({ ...page, htmlContent: html });
    setDirty(true);
    setMessage(`Replaced with ${file.name}. ${live ? "Publish to put the new version live." : "Publish when you're ready."}`);
  };

  const preview = (
    <iframe
      title="Page preview"
      sandbox="allow-scripts allow-forms allow-popups"
      srcDoc={page?.htmlContent || ""}
      className="w-full h-full bg-white"
      style={{ border: 0, minHeight: 600, display: "block" }}
    />
  );

  const code = (
    <div className="flex w-full flex-col gap-3">
      <p className="text-[13px] text-black/50 dark:text-white/50">
        The page exactly as uploaded. Launch OS never changes it; tracking and links between pages are added each time you publish.
      </p>
      <CodeView value={page?.htmlContent || ""} readOnly height="calc(100vh - 240px)" />
    </div>
  );

  return (
    <Page
      topBar={{
        page: [
          { label: site?.name || "•••", href: siteHref, icon: <Browsers className="text-lg" /> },
          { label: page?.name || "•••" },
        ],
        items: (
          <>
            {page && <StatusPill status={statusOf(page, dirty)} />}
            {!showCode && (
              <MobileResponsiveToggle {...({ onChange: setMobileState, mobileState, setMobileState } as any)} />
            )}
            <ListDropdown
              element={
                <Button
                  label={
                    <div className="flex gap-1">
                      <span>Actions</span>
                      <DownCarrotIcon className="pt-1" />
                    </div>
                  }
                  type={ButtonTypes.OUTLINED}
                  variant={Variants.INFO}
                />
              }
              items={[
                { id: "toggle-code", label: showCode ? "Back to Preview" : "View Code", onClick: () => setShowCode(!showCode) },
                { id: "rename", label: "Rename / Change Address…", onClick: () => setRenameOpen(true) },
                { id: "replace", label: "Replace File…", onClick: () => fileInput.current?.click() },
                { id: "tracking", label: "Tracking Settings", onClick: () => setTrackingOpen(true) },
                { id: "divider", type: "divider" },
                {
                  id: "view",
                  label: "View Published Page",
                  onClick: () =>
                    live && siteAddress
                      ? window.open(`https://${siteAddress}/${page?.pageSettings?.html?.publishedSlug}`, "_blank")
                      : setMessage("This page isn't published yet."),
                },
                { id: "divider", type: "divider" },
                { id: "delete", label: "Delete this Page", onClick: () => setConfirmDelete(true) },
              ]}
            />
            <Button label={<span>{busy || (live ? "Republish" : "Publish")}</span>} onClick={() => setPublishOpen(true)} type={ButtonTypes.OUTLINED} />
          </>
        ),
      }}
    >
      <input
        ref={fileInput}
        type="file"
        accept=".html,.htm,text/html"
        hidden
        onChange={(e) => {
          replaceWith(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      {publishOpen && page && (
        <PublishHtmlModal
          pages={[{ id: page.id, name: page.name, slug: page.slug }]}
          siteAddress={siteAddress}
          setIsOpen={setPublishOpen}
          onPublished={async () => {
            setDirty(false);
            await load();
          }}
        />
      )}
      {trackingOpen && <PageTrackingCodesModal setIsOpen={setTrackingOpen} pageId={pageId} />}
      {renameOpen && page && (
        <RenameHtmlPageModal
          page={page}
          campaignId={campaignId}
          siteAddress={siteAddress}
          setIsOpen={setRenameOpen}
          onSaved={async () => {
            await load();
            if (live) {
              setDirty(true);
              setMessage("Saved. Publish to move the live page to its new address.");
            }
          }}
        />
      )}
      {confirmDelete && page && (
        <DeleteHtmlPageModal
          page={page}
          siteAddress={siteAddress}
          setIsOpen={setConfirmDelete}
          onDeleted={() => navigate(siteHref)}
        />
      )}
      {message && (
        <div className="mx-7 mt-4 rounded-lg px-4 py-2 text-sm bg-black/5 dark:bg-white/5 text-black/70 dark:text-white/70">
          {message}
        </div>
      )}
      <div
        className={`w-full h-full flex ${mobileState.type === MobileState.FULLSCREEN && !showCode ? "" : "p-7"}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          replaceWith(e.dataTransfer.files?.[0]);
        }}
      >
        {showCode ? (
          code
        ) : (
          // Full width so the tablet / phone mockups center like they do in the Launch Pages editor.
          <div className="w-full h-full overflow-auto">
            <BrowserWrapper mobileState={mobileState} size={BrowserSize.LARGE} url={siteAddress ? `${siteAddress}/${page?.slug || ""}` : undefined}>
              {preview}
            </BrowserWrapper>
          </div>
        )}
      </div>
    </Page>
  );
};
